import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Heart,
  LayoutDashboard,
  Palette,
  Users,
  ClipboardCheck,
  ExternalLink,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { coupleAuth } from '../../services/api';

const navLinks = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/couple/dashboard' },
  { label: 'Page Builder', icon: Palette, path: '/couple/builder' },
  { label: 'Guest List', icon: Users, path: '/couple/guests' },
  { label: 'RSVPs', icon: ClipboardCheck, path: '/couple/rsvps' },
];

function CoupleNavigation({ couple, isPublished, onPublishToggle, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    coupleAuth.logout();
    navigate('/couple/login');
  };

  const coupleNames = couple
    ? [couple.partner1_name, couple.partner2_name].filter(Boolean).join(' & ')
    : '';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div
        className="flex items-center gap-2 px-5 py-5 cursor-pointer"
        onClick={() => navigate('/couple/dashboard')}
      >
        <div className="w-8 h-8 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center">
          <Heart className="w-5 h-5 text-white" />
        </div>
        <span className="text-lg font-bold bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">
          BridalConnect
        </span>
      </div>

      {/* Navigation links */}
      <nav className="flex-1 px-3 py-2 space-y-1">
        {navLinks.map((link) => {
          const isActive = location.pathname === link.path;
          const Icon = link.icon;
          return (
            <button
              key={link.path}
              onClick={() => {
                navigate(link.path);
                setMobileOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-rose-50 text-rose-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'text-rose-500' : ''}`} />
              {link.label}
            </button>
          );
        })}

        {/* Divider */}
        <div className="border-t border-gray-200 my-3" />

        {/* Preview Page */}
        {couple?.slug && (
          <a
            href={`/${couple.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            Preview Page
          </a>
        )}

        {/* Publish toggle */}
        <button
          onClick={onPublishToggle}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-50"
        >
          <div
            className={`w-9 h-5 rounded-full relative transition-colors ${
              isPublished ? 'bg-rose-500' : 'bg-gray-300'
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                isPublished ? 'left-4' : 'left-0.5'
              }`}
            />
          </div>
          <span>{isPublished ? 'Published' : 'Unpublished'}</span>
        </button>

        {/* Divider */}
        <div className="border-t border-gray-200 my-3" />

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </nav>

      {/* Couple names at bottom */}
      {coupleNames && (
        <div className="px-5 py-4 border-t border-gray-200">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Your Wedding</p>
          <p className="text-sm font-semibold text-gray-700 truncate">{coupleNames}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-[250px] bg-white border-r border-gray-200 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center">
            <Heart className="w-4 h-4 text-white" />
          </div>
          <span className="text-base font-bold bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">
            BridalConnect
          </span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile slide-in sidebar */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 w-[280px] bg-white z-50 transform transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </div>

      {/* Main content */}
      <main className="md:ml-[250px] pt-16 md:pt-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}

export default CoupleNavigation;
